import { observer } from "mobx-react";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { AppBar } from "@/components-v2/AppBar";
import { useAppBar } from "@/appBar/useAppBar";
import { TabProps, Tabs } from "@/components-v2/Tabs";
import { Fragment, useEffect, useMemo } from "react";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Dialog } from "@/components-v2/Dialog";
import { Language } from "@/i18n/languages";
import { Checkbox } from "@/components-v2/Checkbox";
import {
  useDispatch,
  useLanguage,
  usePortfolioStore,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { Box, Card, Container, Grid, GridItem, VBox } from "@/components-v2/Box";
import { DataTable, DataTableProps, TableColumn } from "@/components-v2/DataTable";
import { AdminItem, CompanyItem, isOk, isPending } from "@/common/common";
import {
  AddAdditionalCompanyForm,
  AdminDialogStore,
  ContactsInitOptions,
  EditEmailForm,
  filterAdminRows,
  initContactsPage,
  InviteNewAdminDialogStore,
  RevokeAccessForm,
  RevokeAllForm,
} from "./contactsDomain";
import { ShareholdersView } from "./shareholders/shareholdersComponents.tsx";
import { prepShareholdersView } from "./shareholders/shareholdersDomain.ts";
import { HoverTooltip } from "@/components-v2/Tooltip";
import { NamedOrganisationNumber } from "@/types/Transactions";
import { isMobileView } from "@/utils/screen";
import { MTextInput } from "@/components-v2/TextInput";
import { MSelect } from "@/components-v2/Select";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { navigateToAdminCompanyPortfolios } from "@/utils/navigation";
import {
  extractPortfolioAdminsUrl,
  extractPortfolioContactsAdminUrl,
  extractPortfolioContactsUrl,
  PortfolioType,
  SelectedPortfolioOption,
  usePortfolio,
} from "@/portfolio/portfolioDomain";
import { EmptyState } from "@/components-v2/EmptyState";
import { SendInvitationDialog } from "@/views/Users/SendInvitationDialog";
import { navigate } from "@/events/eventsDomain";
import { LastLogin } from "@/common/commonComponents";
import { RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { toDate } from "@/utils/Dates";
import { Alert } from "@/components-v2/Alert";
import { CustomRoleItem } from "@/common/customRole";
import { CustomRoleView } from "./customRoleComponents";
import {
  CustomRoleStore,
  getRoleNameByLanguage,
  prepCustomRoleUsersTableData,
} from "./customRoleDomain";
import {
  AggregatedContactDetailsPageProps,
  aggregatedDetailsFieldTypeLabel,
  EditDetailsFieldDialogProps,
} from "./aggregatedContactDetailsDomain";
import { CompanyRolesView } from "./companyRolesComponents";
import { compact } from "lodash";
import { InfoTag } from "@/components-v2/InfoTag";
import { prepCompanyRolesView } from "./companyRolesDomain";
import { PhoneInput } from "@/components-v2/PhoneInput";
import { RolesDialogs } from "@/companyRoles/companyRolesDialogsComponents.tsx";
import { CompanySelector } from "@/portfolio/portfolioComponents";

type PortfolioContactsTab =
  | { type: "shareholders" }
  | { type: "admins" }
  | { type: "board" }
  | {
      type: "custom-role";
      customRoleId: string;
    };

const mapToContactsTab = (customRoleId: string, tab?: string): PortfolioContactsTab => {
  switch (tab) {
    case "admins":
      return { type: "admins" };
    case "board":
      return { type: "board" };
    case "custom-role":
      return { type: "custom-role", customRoleId };
    default:
      return { type: "shareholders" };
  }
};

export const PortfolioContactsPage = observer(function PortfolioContactsPage({
  portfolioType,
  tab,
  portfolioId,
  customRoleId,
}: {
  portfolioType: PortfolioType;
  portfolioId?: string;
  tab?: string;
  customRoleId?: string;
}) {
  const { portfolioStore, messageStore, dispatch, apiService, userStore, contacts, location } =
    useRootStore();
  const id = customRoleId ?? "";
  const locale = useLanguage();
  const contactsTab = mapToContactsTab(id, tab);
  const { t } = useTranslation();
  const { portfolio } = usePortfolio({
    activeHeader: "contacts",
    portfolioId,
    portfolioType,
  });

  const contactsOptionState = location.state as ContactsInitOptions | null;

  useEffect(() => {
    portfolioStore.fetchPortfolioAdmins(portfolio);
    portfolioStore.initCompanyInAdminsFilter();
    portfolioStore.fetchPortfolioCustomRole(portfolio, portfolioId);
    dispatch(initContactsPage(portfolio, contactsOptionState ?? undefined));
    return () => {
      portfolioStore.resetPortfolioAdmins();
      portfolioStore.resetPortfolioCustomRole();
    };
  }, [portfolioStore, portfolio, portfolioId, dispatch, contactsOptionState]);

  const appBar = useAppBar({
    isPortfolioPage: true,
    portfolioHeader: "contacts",
    selectedPortfolioOption: portfolio,
  });
  const adminsResult = portfolioStore.fetchPortfolioAdminsResult;
  const admins = isOk(adminsResult) ? adminsResult.data.admins : [];

  const customRoleItemResult = portfolioStore.fetchPortfolioCustomRoleResult;
  const customRoleItems = isOk(customRoleItemResult)
    ? customRoleItemResult.data.customRoleItems
    : [];

  const store = useMemo(() => {
    if (isOk(customRoleItemResult) && customRoleId !== undefined) {
      const { data } = customRoleItemResult;
      const item = data.customRoleItems.find((r) => r.role.customRoleId === customRoleId);
      if (item) {
        const creatorCompany = userStore.companiesWithAdminAccess.find(
          (adminCompany) => adminCompany.organisationNumber === item.role.orgNo,
        );
        return new CustomRoleStore({
          customRoleItem: item,
          availableCompanies: data.availableCompanies,
          dispatch: dispatch,
          apiService,
          selectedPortfolioOption: portfolio,
          locale,
          showEmailField: true,
          user: userStore.user,
          refreshUserInformation: userStore.refreshUserInformation,
          access: { companies: userStore.companies, userRoles: userStore.userRoles },
          creatorCompany,
        });
      }
    }
  }, [
    customRoleItemResult,
    customRoleId,
    dispatch,
    apiService,
    portfolio,
    locale,
    userStore.user,
    userStore.refreshUserInformation,
    userStore.companies,
    userStore.userRoles,
    userStore.companiesWithAdminAccess,
  ]);

  function renderShareholders() {
    if (!contacts || isPending(contacts.response)) {
      return (
        <VBox grow={"golden"} justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    }
    if (isOk(contacts.response)) {
      return (
        <ShareholdersView
          {...prepShareholdersView({
            state: contacts,
            dispatch,
            portfolio,
          })}
        />
      );
    }
    return undefined;
  }

  function renderAdmins() {
    if (adminsResult === undefined || isPending(adminsResult)) {
      return (
        <VBox fullheight justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    } else if (isOk(adminsResult)) {
      const companyItems = adminsResult.data.availableCompanies;
      return (
        <>
          {adminsResult.data.admins.length > 0 ? (
            <AdminsView
              admins={adminsResult.data.admins}
              adminsSearch={portfolioStore.adminNameFilter}
              companyFilter={portfolioStore.companyInAdminsFilter}
              openInviteNewAdminDialog={
                companyItems.length === 0
                  ? undefined
                  : (lang) =>
                      dispatch([
                        {
                          type: "showInviteNewAdminDialog",
                          lang,
                          portfolio,
                        },
                      ])
              }
              navigateToAdminView={(externalId) =>
                dispatch([
                  {
                    type: "navigate",
                    url: extractPortfolioAdminsUrl(portfolio, externalId),
                  },
                ])
              }
              openEmailEdit={(externalId, email, adminName) =>
                portfolioStore.openEditEmailDialog({
                  id: externalId,
                  email,
                  onSuccess: (updatedEmail) => {
                    const index = adminsResult.data.admins.findIndex((e) => e.id === externalId);
                    adminsResult.data.admins[index].email = updatedEmail;
                  },
                  name: adminName,
                })
              }
              openRevokeAll={(orgNos, externalId, name) =>
                portfolioStore.openRevokeAllDialog({
                  orgNos,
                  externalId,
                  name,
                  onSuccess: () => {
                    portfolioStore.onRevokeAllAdminAccess(externalId);
                  },
                  portfolio,
                })
              }
            />
          ) : (
            <AdminEmptyView
              title={t("contacts.admins.empty-state.title")}
              openInviteNewAdminDialog={
                companyItems.length === 0
                  ? undefined
                  : (lang) =>
                      dispatch([
                        {
                          type: "showInviteNewAdminDialog",
                          lang,
                          portfolio,
                        },
                      ])
              }
              showCompanyEmptyState={portfolioStore.allCompanies.length > 0}
              portfolio={portfolio}
            />
          )}
        </>
      );
    } else {
      return undefined;
    }
  }

  function renderCompanyRole() {
    if (!contacts || isPending(contacts.response)) {
      return (
        <VBox grow={"golden"} justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      );
    }
    if (isOk(contacts.response)) {
      return (
        <CompanyRolesView
          {...prepCompanyRolesView({
            state: contacts,
            dispatch,
            portfolio,
          })}
        />
      );
    }
    return undefined;
  }

  function renderCustomRole() {
    if (customRoleItemResult === undefined || isPending(customRoleItemResult)) {
      return (
        <VBox fullheight justifyContent="center">
          <LoadingIndicator />
        </VBox>
      );
    }
    if (store !== undefined) {
      return <CustomRoleView store={store} />;
    }
    return undefined;
  }

  return (
    <FullScreenLayout
      alertStore={messageStore}
      pageHeader={
        <AppBar
          data={appBar}
          subHeaderElement={
            isOk(contacts?.response) ? (
              <ContactsHeader
                numberOfShareholders={
                  contacts.response.data.contacts.filter((r) => r.ownershipItems.length > 0).length
                }
                numberOfAdminsInCompany={admins.length}
                contactsTab={contactsTab}
                portfolio={portfolio}
                roleItems={customRoleItems}
                locale={locale}
                numberOfBoard={
                  contacts.response.data.contacts.filter((r) => r.roleItems.length > 0).length
                }
              />
            ) : undefined
          }
        />
      }
      centerPanel={
        contactsTab.type === "admins"
          ? renderAdmins()
          : contactsTab.type === "shareholders"
            ? renderShareholders()
            : contactsTab.type === "board"
              ? renderCompanyRole()
              : renderCustomRole()
      }
    />
  );
});

const mapToContactsUrl = (portfolio: SelectedPortfolioOption, tab: PortfolioContactsTab) =>
  tab.type === "custom-role"
    ? `${extractPortfolioContactsUrl(portfolio)}/${tab.type}/${tab.customRoleId}`
    : `${extractPortfolioContactsUrl(portfolio)}/${tab.type}`;

const ContactsHeader = observer(function ContactsHeader({
  numberOfShareholders,
  contactsTab,
  portfolio,
  numberOfAdminsInCompany,
  roleItems,
  locale,
  numberOfBoard,
}: {
  numberOfShareholders?: number;
  numberOfAdminsInCompany?: number;
  numberOfBoard?: number;
  contactsTab: PortfolioContactsTab;
  portfolio: SelectedPortfolioOption;
  roleItems?: CustomRoleItem[];
  locale: Language;
}) {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const tabs: TabProps[] = compact([
    {
      id: "shareholders",
      label: t("contacts.shareholders", { count: numberOfShareholders }),
      onSelect: () => history.push(mapToContactsUrl(portfolio, { type: "shareholders" })),
      isActive: contactsTab.type === "shareholders",
    },
    {
      id: "admins",
      label: t("contacts.admins", { count: numberOfAdminsInCompany }),
      onSelect: () => history.push(mapToContactsUrl(portfolio, { type: "admins" })),
      isActive: contactsTab.type === "admins",
    },
    {
      id: "companyRoles",
      label: t("contacts.company-roles", { count: numberOfBoard }),
      onSelect: () => history.push(mapToContactsUrl(portfolio, { type: "board" })),
      isActive: contactsTab.type === "board",
      disabled: numberOfBoard === 0,
    },
    ...(roleItems
      ? roleItems
          .filter((f) => f.companies.length > 1)
          .map<TabProps>((roleItem) => ({
            id: roleItem.role.customRoleId,
            key: roleItem.role.customRoleId,
            label: `${getRoleNameByLanguage(roleItem.role, locale)} (${prepCustomRoleUsersTableData({ users: roleItem.availableUsers, companies: roleItem.companies }).length})`,
            onSelect: () =>
              history.push(
                mapToContactsUrl(portfolio, {
                  type: "custom-role",
                  customRoleId: roleItem.role.customRoleId,
                }),
              ),
            isActive:
              contactsTab.type === "custom-role" &&
              contactsTab.customRoleId === roleItem.role.customRoleId,
          }))
      : []),
  ]);
  return (
    <Box bg>
      <VBox maxWidth="xl" container marginTop="lg">
        <h4>{t("header.contacts")}</h4>
        <Tabs tabs={tabs} />
      </VBox>
    </Box>
  );
});

function AdminDropdownMenu({
  onNavigateToAdmin,
  openEditEmail,
  openRevokeAll,
}: {
  onNavigateToAdmin: () => void;
  openEditEmail: () => void;
  openRevokeAll: () => void;
}) {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      button={{ type: "dotdotdot" }}
      alignment={"end"}
      items={[
        {
          type: "item",
          label: t("contacts.admins.manage-permissions"),
          icon: "user-list",
          onSelect: () => onNavigateToAdmin(),
        },
        {
          type: "item",
          label: t("contacts.admins.edit-email"),
          icon: "pen",
          onSelect: () => openEditEmail(),
        },
        {
          type: "item",
          label: t("contacts.admins.revoke-all"),
          icon: "trash",
          color: "error",
          onSelect: () => openRevokeAll(),
        },
      ]}
    />
  );
}

const AdminsView = observer(function AdminsView({
  admins,
  adminsSearch,
  companyFilter,
  openInviteNewAdminDialog,
  navigateToAdminView,
  openEmailEdit,
  openRevokeAll,
}: {
  admins: AdminItem[];
  adminsSearch: StringFieldModel;
  companyFilter?: RadioFieldModel<NamedOrganisationNumber | null>;
  openInviteNewAdminDialog?: (language: Language) => void;
  navigateToAdminView: (externalId: string) => void;
  openEmailEdit: (externalId: string, email: string, adminName: string) => void;
  openRevokeAll: (orgNos: string[], externalId: string, name: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLanguage();
  const columns: TableColumn<AdminItem>[] = [
    {
      name: "name",
      label: t("name"),
      align: "left",
    },
    {
      name: "companies",
      label: t("portfolioContacts.companies"),
      align: "left",
      getValue: (row) => <ContactAdminsCompaniesData companies={row.companies} />,
    },
    {
      name: "email",
      label: t("email"),
      align: "left",
    },
    {
      name: "",
      label: t("portfolioContacts.last-login"),
      align: "left",
      getValue: (row) => <LastLogin lastLogin={row.lastSeen} />,
      getSortValue: (row) => (row.lastSeen ? toDate(row.lastSeen).getTime() : 0),
    },
    {
      name: "menu",
      label: "",
      align: "right",
      getValue: (row) => (
        <AdminDropdownMenu
          onNavigateToAdmin={() => navigateToAdminView(row.id)}
          openEditEmail={() => openEmailEdit(row.id, row.email, row.name)}
          openRevokeAll={() =>
            openRevokeAll(
              row.companies.map((a) => a.organisationNumber),
              row.id,
              row.name,
            )
          }
        />
      ),
    },
  ];
  return (
    <Container padding maxWidth="xl">
      <Box vertical={isMobileView()}>
        <Box vertical={isMobileView()}>
          <MTextInput
            model={adminsSearch}
            icon={"magnifying-glass"}
            placeholder={t("portfolioContacts.nameFilterPlaceholder")}
            type={"search"}
          />
          {companyFilter && (
            <Box>
              <MSelect model={companyFilter} />
            </Box>
          )}
        </Box>
        {openInviteNewAdminDialog && (
          <Box marginLeft={isMobileView() ? false : "auto"}>
            <Button
              variant={"secondary"}
              label={t("contacts.admins.invite-new-admin")}
              icon={"plus"}
              onClick={() => openInviteNewAdminDialog(locale)}
            />
          </Box>
        )}
      </Box>
      <DataTable
        onRowClick={(row) => navigateToAdminView(row.id)}
        variant={"LARGE"}
        tableColumns={columns}
        tableData={filterAdminRows(admins, adminsSearch.inputValue, companyFilter?.value)}
        getId={(row) => row.id}
      />
    </Container>
  );
});

function ContactAdminsCompaniesData({ companies }: { companies: CompanyItem[] }) {
  const { t } = useTranslation();
  return companies.length === 1 ? (
    <Box>
      <div>{companies[0].companyName}</div>
    </Box>
  ) : (
    <Box>
      <HoverTooltip
        content={
          <div>
            {companies.map((c) => (
              <Box key={c.organisationNumber} whitespace={"nowrap"}>
                <div>{c.companyName}</div>
              </Box>
            ))}
          </div>
        }
      >
        {({ targetRef }) => (
          <Box ref={targetRef} className={"underline decoration-dotted"}>
            {t("portfolio.numberOfCompanies", { count: companies.length })}
          </Box>
        )}
      </HoverTooltip>
    </Box>
  );
}

export const InviteNewAdminDialog = observer(function InviteNewAdminDialog({
  store,
}: {
  store: InviteNewAdminDialogStore;
}) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("invite-user.title")}
      submitLabel={t("invite-user.send-invite")}
      cancelLabel={t("cancel")}
      onSubmit={() => store.submitInviteNewAdminDialog()}
      onCancel={store.onClose}
      isSubmitDisabled={store.isSubmitDisabled}
      isSubmitting={store.isLoading}
    >
      <VBox gap={"md"}>
        <MTextInput label={t(`invite-user.email`)} model={store.email} />
        <MSelect model={store.language} label={t("invite-user.choose-language-for-email")} />
        <CompanySelector model={store.companySelector} label={t("portfolioContacts.companies")} />
      </VBox>
    </Dialog>
  );
});

export const AdminEmptyView = observer(
  ({
    title,
    description,
    openInviteNewAdminDialog,
    showCompanyEmptyState,
    portfolio,
  }: {
    title: string;
    description?: string;
    openInviteNewAdminDialog?: (language: Language) => void;
    showCompanyEmptyState: boolean;
    portfolio: SelectedPortfolioOption;
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const history = useRootStore().history;
    return (
      <VBox grow={"golden"} justifyContent={"center"}>
        {showCompanyEmptyState ? (
          <EmptyState
            illustration={"briefcase"}
            title={t("portfolio.empty-active-companies.title")}
            description={description}
            actions={
              <Button
                variant={"secondary"}
                label={t("contacts.admins.empty-state.add-to-portfolio")}
                onClick={() => navigateToAdminCompanyPortfolios(portfolio, history)}
              />
            }
          />
        ) : (
          <EmptyState
            illustration={"user-list"}
            title={title}
            description={description}
            actions={
              openInviteNewAdminDialog && (
                <Button
                  variant={"secondary"}
                  label={t("contacts.admins.invite-new-admin")}
                  icon={"plus"}
                  onClick={() => openInviteNewAdminDialog(locale)}
                />
              )
            }
          />
        )}
      </VBox>
    );
  },
);

export const AdminInfoPage = observer(
  ({
    externalId,
    portfolioType,
    portfolioId,
    activeHeader,
  }: {
    portfolioType: PortfolioType;
    externalId?: string;
    portfolioId?: string;
    activeHeader: string | undefined;
  }) => {
    const { t } = useTranslation();
    const { portfolio } = usePortfolio({ activeHeader, portfolioType, portfolioId });
    const { portfolioStore, messageStore, dispatch, apiService, history, http, userStore } =
      useRootStore();

    useEffect(() => {
      portfolioStore.fetchPortfolioAdmins(portfolio);
      return () => {
        portfolioStore.resetPortfolioAdmins();
      };
    }, [portfolio, portfolioStore]);

    const adminsResult = portfolioStore.fetchPortfolioAdminsResult;
    const admin = isOk(adminsResult)
      ? adminsResult.data.admins.find((a) => a.id === externalId)
      : undefined;

    const store = useMemo(
      () =>
        admin && isOk(adminsResult)
          ? new AdminDialogStore(
              admin,
              () => dispatch([{ type: "closeDialog" }]),
              apiService,
              portfolio,
              messageStore,
              history,
              adminsResult.data.availableCompanies,
              http,
              userStore.user,
            )
          : undefined,
      [
        admin,
        adminsResult,
        apiService,
        dispatch,
        history,
        messageStore,
        portfolio,
        http,
        userStore.user,
      ],
    );

    const renderEditDialog = () => {
      switch (store?.editDialogState?.type) {
        case "resendInvite":
          return <SendInvitationDialog store={store.editDialogState.form} />;
        case "revokeAccess":
          return <RevokeAccessInAdminForm store={store.editDialogState.form} />;
        case "addAdditionalCompany":
          return <AddCompanyForm store={store.editDialogState.form} />;
        default:
          return null;
      }
    };
    return (
      <FullScreenLayout
        pageHeader={<AdminInfoHeader selectedPortfolio={portfolio} adminName={store?.admin.name} />}
        centerPanel={
          store === undefined ? (
            isOk(adminsResult) ? (
              <EmptyState
                illustration={"user-list"}
                title={t("notFound")}
                actions={
                  <Button
                    variant={"secondary"}
                    label={t("back")}
                    onClick={() => history.push(mapToContactsUrl(portfolio, { type: "admins" }))}
                  />
                }
              />
            ) : (
              <VBox grow={"golden"} justifyContent={"center"}>
                <LoadingIndicator />
              </VBox>
            )
          ) : (
            <>
              <AdminInfoBody
                admin={store.admin}
                store={store}
                onSuccess={() => portfolioStore.onRevokeAllAdminAccess(store.admin.id)}
                portfolio={portfolio}
              />
              {renderEditDialog()}
            </>
          )
        }
      />
    );
  },
);

const AdminInfoHeader = observer(function AdminInfoHeader({
  selectedPortfolio,
  adminName,
}: {
  selectedPortfolio: SelectedPortfolioOption;
  adminName?: string;
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  return (
    <Box
      bg={"default"}
      grow
      alignItems={"center"}
      paddingLeft={"md"}
      paddingRight={"lg"}
      paddingY={"md"}
      borderBottom
    >
      <IconButton
        icon={"x"}
        tooltip={t("back")}
        onClick={() => dispatch([navigate(extractPortfolioContactsAdminUrl(selectedPortfolio))])}
      />
      <div>{adminName}</div>
      <div>-</div>
      <div>{t(selectedPortfolio.label)}</div>
    </Box>
  );
});

const AdminInfoBody = observer(function AdminInfoBody({
  admin,
  store,
  onSuccess,
  portfolio,
}: {
  admin: AdminItem;
  store: AdminDialogStore;
  onSuccess: () => void;
  portfolio: SelectedPortfolioOption;
}) {
  const { t } = useTranslation();
  const { openEditEmailDialog, openRevokeAllDialog } = usePortfolioStore();
  return (
    <VBox maxWidth="lg" gap={"md"} container marginTop="lg">
      <h2>{admin.name}</h2>
      <Card title={t("contacts.admins.details")} alignItems={"start"}>
        <Grid>
          <GridItem xs={12} lg={4}>
            {t("email")}
          </GridItem>
          <GridItem xs={12} lg={8}>
            <Box>
              <div>{admin.email}</div>
              <LinkButton
                label={t("edit")}
                icon={"edit"}
                onClick={() =>
                  openEditEmailDialog({
                    id: admin.id,
                    email: admin.email,
                    onSuccess: (updateEmail) => store.onSuccessUpdateEmail(updateEmail),
                    name: admin.name,
                  })
                }
              />
            </Box>
          </GridItem>
        </Grid>
      </Card>
      <AdminInCompaniesCard
        store={store}
        openRevokeAllDialog={() =>
          openRevokeAllDialog({
            orgNos: admin.companies.map((f) => f.organisationNumber),
            externalId: admin.id,
            name: admin.name,
            onSuccess,
            portfolio,
          })
        }
      />
    </VBox>
  );
});

const AdminInCompaniesCard = observer(
  ({
    store,
    openRevokeAllDialog,
  }: {
    store: AdminDialogStore;
    openRevokeAllDialog: () => void;
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <Card
        title={
          store.admin.companies.length > 1
            ? t("contacts.admins.title", {
                n: store.admin.companies.length,
              })
            : t("contacts.admins.title-one")
        }
        action={
          store.admin.companies.length > 1 ? (
            <LinkButton
              color={"error"}
              label={t("contacts.admins.revoke-all")}
              onClick={() => openRevokeAllDialog()}
            />
          ) : undefined
        }
      >
        {store.admin.companies.map((c) => (
          <Fragment key={c.organisationNumber}>
            <Box vertical={isMobileView()} marginTop={"md"}>
              <div className={"grow"}>{c.companyName}</div>
              <Box gap={"md"} alignItems={"end"}>
                <LinkButton
                  label={t("contacts.admins.resend-invite")}
                  icon={"paper-plane-tilt"}
                  onClick={() => store.openResendInviteDialog(locale, c, c.organisationNumber)}
                />
                <LinkButton
                  label={t("contacts.admins.revoke-access")}
                  icon={"trash"}
                  onClick={() => {
                    store.openRevokeAccessDialog(c.organisationNumber, c.companyName, () =>
                      navigateToAdminCompanyPortfolios(
                        store.selectedPortfolioOption,
                        store.history,
                      ),
                    );
                  }}
                />
              </Box>
            </Box>
            <hr />
          </Fragment>
        ))}
        <Box marginTop={"md"}>
          <LinkButton
            label={t("portfolio.welcome.add-company")}
            icon={"plus"}
            onClick={() => store.openAddAdditionalCompanyDialog(locale)}
          />
        </Box>
      </Card>
    );
  },
);

export const EmailEditForm = observer(({ store }: { store: EditEmailForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("contacts.admins.edit-email-title", { name: store.adminName })}
      cancelLabel={t("cancel")}
      onCancel={store.closeEditDialog}
      submitLabel={t("save")}
      onSubmit={() => store.updateAdministratorEmail()}
      isSubmitDisabled={store.isSubmitDisabled}
      isSubmitting={store.isLoading}
    >
      <MTextInput model={store.email} label={t("email")} autoSelect />
    </Dialog>
  );
});

const RevokeAccessInAdminForm = observer(({ store }: { store: RevokeAccessForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("settings.revokeAdminAccess")}
      submitLabel={t("confirm")}
      onSubmit={() => store.confirmRevokeAccessAdmin()}
      onCancel={() => store.closeRevokeAccessDialog()}
      isSubmitting={store.isLoading}
    >
      <div>
        {t("settings.revokeAdminAccessForQuestion", {
          name: store.name,
        })}
      </div>
    </Dialog>
  );
});

export const RevokeAllInAdminForm = observer(({ store }: { store: RevokeAllForm }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("settings.revokeAdminAccess")}
      submitLabel={t("confirm")}
      onSubmit={() => store.confirmRevokeAll()}
      onCancel={() => store.closeRevokeAccessDialog()}
      isSubmitting={store.isLoading}
    >
      <div>{t("settings.revokeAdminAccessForQuestion", { name: store.name })}</div>
    </Dialog>
  );
});

const AddCompanyForm = observer(({ store }: { store: AddAdditionalCompanyForm }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      title={t("contacts.admins.add-additional-company-title", { name: store.admin.name })}
      submitLabel={t("invite-user.send-invite")}
      cancelLabel={t("cancel")}
      onSubmit={() => store.submitAddCompanyInAdminDialog()}
      onCancel={store.onClose}
      isSubmitDisabled={store.isSubmitDisabled}
      isSubmitting={store.isLoading}
    >
      <>
        {store.isAlreadyAdminInAllCompanies ? (
          <div>
            {t({
              key: "contacts.admins.already-admin-in-all-companies-in-portfolio",
              params: {
                name: store.admin.name,
                portfolio: store.selectedPortfolioOption.label,
              },
            })}
          </div>
        ) : (
          <VBox gap={"md"}>
            <Box>
              <LinkButton label={t("select-all")} onClick={() => store.selectAllCompanies()} />
              {"/"}
              <LinkButton
                label={t("portfolio.NONE")}
                onClick={() => store.deselectAllCompanies()}
              />
            </Box>
            {store.companyOptions.map((c) => (
              <Checkbox
                key={c.organisationNumber}
                label={
                  <Box>
                    <div>{c.companyName}</div>
                    <small className={"subtle"}>{c.organisationNumber}</small>
                  </Box>
                }
                value={c.organisationNumber}
                checked={store.isSelected(c.organisationNumber)}
                onChange={() => store.toggleSelected(c.organisationNumber)}
              />
            ))}
            <MSelect model={store.language} label={t("invite-user.choose-language-for-email")} />
          </VBox>
        )}
      </>
    </Dialog>
  );
});

export const AggregatedContactDetailsHeader = observer(
  (props: AggregatedContactDetailsPageProps) => {
    return (
      <Box bg>
        <Container maxWidth={"xl"} gap={"lg"}>
          <h3>{props.name}</h3>
          <Box justifyContent={"space-between"}>
            <Box alignItems={"center"} wrap>
              {props.infoTags?.map((it) => (
                <InfoTag {...it} key={it.id} />
              ))}
              <LastLogin lastLogin={props.lastLogin} />
            </Box>
            {props.aggregatedContactDetailsOptionsMenu && (
              <DropdownMenu {...props.aggregatedContactDetailsOptionsMenu} />
            )}
          </Box>
          <Tabs tabs={props.tabs} />
        </Container>
      </Box>
    );
  },
);

export const AggregatedContactDetailsBody = observer((props: AggregatedContactDetailsPageProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Container maxWidth={"xl"} marginTop={"lg"}>
        <VBox gap={"md"}>
          <Box vertical={isMobileView()} className={"self-start"} alignItems={"center"}>
            <MTextInput
              model={props.searchFieldModel}
              icon={"magnifying-glass"}
              placeholder={t("portfolioContacts.nameFilterPlaceholder")}
              type={"search"}
            />
            {props.investmentStatusModel && <MSelect model={props.investmentStatusModel} />}
          </Box>
          <DataTable variant={"COMPACT"} {...(props.table as DataTableProps<unknown>)} />
        </VBox>
      </Container>
      {props.editDialog && <RolesDialogs {...props.editDialog} />}
    </>
  );
});

export const AggregatedContactDetailsEditDialog = observer((props: EditDetailsFieldDialogProps) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t(props.title)}
      cancelLabel={t("cancel")}
      submitLabel={t(props.submitLabel)}
      onCancel={props.onClose}
      onSubmit={props.updateContactsPersonalField}
      isSubmitDisabled={props.isSubmitDisabled}
      isSubmitting={props.isUpdating}
    >
      <VBox gap={"md"}>
        <div>{t("contacts.company-roles.details.enter-new-value")}</div>
        {props.detailsFieldModel.type === "phone" ? (
          <PhoneInput model={props.detailsFieldModel.updateModel} autoFocus />
        ) : (
          <MTextInput
            model={props.detailsFieldModel.updateModel}
            label={t(aggregatedDetailsFieldTypeLabel[props.aggregatedDetailsFieldType])}
          />
        )}
        <div>{t("contacts.company-roles.details.selected.companies.to-update")}</div>
        <Box>
          <LinkButton label={t("select-all")} onClick={props.selectAllCompanies} />
          {"/"}
          <LinkButton
            label={t("none")}
            onClick={props.clearCompanies}
            disabled={props.selectedCompanyModel.selected.length === 0}
          />
        </Box>

        {props.selectedCompanyModel.options.map((item) => (
          <Checkbox
            key={item.id}
            checked={props.selectedCompanyModel.isSelected(item)}
            label={
              <Box alignItems={"center"}>
                <div>{item.companyItem.companyName}</div>
                <div className={"text-base subtle"}>{t(item.subText)}</div>
              </Box>
            }
            onChange={() => {
              props.toggleCompany(item);
            }}
          />
        ))}
        {props.alertMessage && (
          <Alert icon={"info"} type={"error"} message={t(props.alertMessage)} />
        )}
      </VBox>
    </Dialog>
  );
});
